import cn from 'classnames';
import React, { Suspense, useState } from 'react';
import { CreditCards } from 'src/components/credit-cards';
import { LearnMoreHowItWorks } from 'src/components/learn-more-how-it-works';
import { PopupLearnMoreBackgroundHeader } from 'src/components/popup-learn-more-background-header/popup-learn-more-background-header';
import { Item } from 'src/components/popup-learn-more-pay-in-full/components/item';
import { TrustBanner } from 'src/components/trust-banner';
import { Button } from 'src/components/v2/button';
import { COUNTRY_BY_CURRENCY_MAP } from 'src/constants/country-by-currency-map';
import { useAppContext } from 'src/context/store/app-provider';
import { useLocale } from 'src/hooks/use-locale';

import styles from './PayInFullContent.css';

interface Props {
  onClose?: () => void;
  onContinue?: () => void;
}

export const PayInFullContent = ({ onClose, onContinue }: Props) => {
  const { currency, installmentsCount } = useAppContext();
  const country = COUNTRY_BY_CURRENCY_MAP[currency];
  const t = useLocale();

  const [activeIcon, setActiveIcon] = useState<{
    type: 'pay-in' | 'pay-in-full' | 'none';
    status: boolean;
  }>({ type: 'none', status: false });

  return (
    <>
      <PopupLearnMoreBackgroundHeader
        onClose={onClose}
        title={t('default.take_shopping')}
        subtitle={t('default.split_your_purchases', {
          installmentsCount,
        })}
      />

      <>
        <Item
          title={t('default.pay_in', {
            installmentsCount,
          })}
          description={t('default.split_your_purchases_interest_free', {
            installmentsCount,
          })}
          iconType="pie"
          selected={activeIcon.type === 'pay-in' && activeIcon.status}
          onToggle={() => {
            if (activeIcon.type === 'pay-in') {
              setActiveIcon({ type: 'pay-in', status: !activeIcon.status });
            } else {
              setActiveIcon({ type: 'pay-in', status: true });
            }
          }}
        >
          <Suspense fallback={null}>
            <LearnMoreHowItWorks
              classNameRoot={styles.PayInFullContent__HowItWorks}
            />
          </Suspense>
        </Item>

        <Item
          title={t('default.pay_in_full')}
          description={t('default.pay_full_amount_today')}
          iconType="card"
          selected={activeIcon.type === 'pay-in-full' && activeIcon.status}
          onToggle={() => {
            if (activeIcon.type === 'pay-in-full') {
              setActiveIcon({
                type: 'pay-in-full',
                status: !activeIcon.status,
              });
            } else {
              setActiveIcon({ type: 'pay-in-full', status: true });
            }
          }}
        >
          <Suspense fallback={null}>
            <LearnMoreHowItWorks
              classNameRoot={styles.PayInFullContent__HowItWorks}
              list={[
                { text: t('choose_tabby_at_checkout') },
                {
                  text: t(
                    'tabbySimple.enter_your_information_add_your_debit_or_credit_card'
                  ),
                },
                { text: t('pay_for_your_purchase_in_full') },
              ]}
            />
          </Suspense>
        </Item>

        {country === 'KSA' ? (
          <TrustBanner
            title={t('shariah_compliant_payments')}
            description={t(
              'our_payment_options_meet_islamic_finance_standards'
            )}
            icon="shariat-modern"
            iconClassName={styles.PayInFullContent__BannerIcon}
          />
        ) : (
          <TrustBanner
            title={t('tabby_trusted.trusted_by_milions')}
            description={t('over_20_million_shoppers_pay_with_tabby')}
            icon="people"
          />
        )}

        <TrustBanner
          title={t('tabby_care.show_safely')}
          description={t('tabby_care.buyer_protection_included')}
          icon="tabby-care"
        />

        {onContinue && (
          <Button
            onClick={onContinue}
            data-test="continue-shopping"
            className={styles.PayInFullContent__ContinueShopping}
          >
            {t('tabbySplit.continue_shopping')}
          </Button>
        )}

        <CreditCards
          country={country}
          fullWidth
          className={cn(
            onContinue ? null : styles.PayInFullContent__CreditCards
          )}
        />
      </>
    </>
  );
};
